import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import EmojiPicker from 'emoji-picker-react';
import {
  Send, AlertTriangle, Paperclip, Smile, Tag, X, MoreHorizontal, CheckCircle2, ArrowRightLeft,
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Palette, Eraser, Lock, MessageSquare,
  Clock, Users, Bot as BotIcon, Inbox, Headphones
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import {
  liveChatStatusLabel, toMs, visitorName, channelOf, CHANNEL_LABELS, isWaiting, isLive, shortAgo, historyText
} from '../utils/liveChat';
import ReassignChat from '../components/ReassignChat';
import PresenceSwitch from '../components/PresenceSwitch';
import ChatList, { ChannelIcon, TABS, OPEN_SEARCH_EVENT } from '../components/live-chat/ChatList';
import DetailsPanel from '../components/live-chat/DetailsPanel';
import MessageList from '../components/live-chat/MessageList';
import AwayBar, { PRESENCE_EVENT } from '../components/live-chat/AwayBar';
import { Avatar, Badge, Button, Kbd, cn } from '../components/ui';

const PAGE_SIZE = 20;
const TEAM_REFRESH_MS = 30000;
const SEEN_KEY = 'lc_seen_v1';
const VIEW_KEY = 'lc_view';

const timeLabel = (ms) => new Date(ms || Date.now()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });

const cleanText = (text) => String(text)
  .replace(/\s*<additional context>[\s\S]*?<\/additional context>\s*/g, '')
  .replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '')
  .replace(/[ \t]+/g, ' ')
  .trim();

const loadSeen = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || 'null'); } catch (e) { return null; }
};

// Short beep for a new waiting chat (no audio file needed)
const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => ctx.close();
  } catch (e) {
    // sound blocked by the browser: the visual signals remain
  }
};

const StatTile = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-4">
    <div className="flex items-center gap-2 text-xs text-zinc-500">
      <Icon className={cn('h-3.5 w-3.5', tone)} />
      {label}
    </div>
    <div className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">{value ?? '–'}</div>
  </div>
);

// ===== LIVE CHAT SCREEN =====
const HumanAgentChatInterface = ({ onLogout, user }) => {
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const getActiveAiAgentId = useCallback(() => {
    const stored = localStorage.getItem('activeAiAgentId');
    if (stored && stored !== 'None' && stored !== 'null' && stored !== 'undefined') {
      return stored;
    }
    if (user) {
      if (user.role !== 'admin' && user.role !== 'super_admin') {
        const assigned = user.assigned_ai_agents || [];
        return user.ai_agent_id?.toString() || (assigned.length > 0 ? assigned[0].toString() : null);
      }
      if (user.ai_agent_id && user.ai_agent_id !== "all") {
        return user.ai_agent_id.toString();
      }
      const assigned = user.assigned_ai_agents || [];
      if (assigned.length > 0) {
        return assigned[0].toString();
      }
    }
    return null;
  }, [user]);

  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem('human_agent_interface_session_id');
    if (stored && stored !== 'None' && stored !== 'null') return stored;
    return null;
  });
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const [view, setView] = useState(() => {
    const v = localStorage.getItem(VIEW_KEY);
    return TABS.some(t => t.id === v) ? v : 'waiting';
  });
  const [counts, setCounts] = useState(null);
  const [team, setTeam] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [seen, setSeen] = useState(() => loadSeen() || {});
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [privateNoteMode, setPrivateNoteMode] = useState(false);

  // Ref Set to track message texts synchronously to avoid React state batching race conditions (duplicate messages)
  const addedMessageTextsRef = React.useRef(new Set());

  React.useEffect(() => {
    addedMessageTextsRef.current.clear();
  }, [sessionId]);
  const [localTags, setLocalTags] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const editorRef = useRef(null);
  const headerMenuRef = useRef(null);
  const searchRef = useRef(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState('#111827');
  const [activeFormat, setActiveFormat] = useState(null);
  const [activeFormats, setActiveFormats] = useState({});
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);

  // Handoff state
  const [isHandoffAccepted, setIsHandoffAccepted] = useState(false);
  const [isTakenOver, setIsTakenOver] = useState(false);
  const [takenOverBy, setTakenOverBy] = useState(null);
  const [takenOverByName, setTakenOverByName] = useState(null);
  const [isInactive, setIsInactive] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  const isInactiveRef = useRef(false);

  const messagesBoxRef = useRef(null);

  // scroll only the transcript (scrollIntoView would also scroll the page around it)
  useEffect(() => {
    const box = messagesBoxRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // clock for waiting timers
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  // ---------- chat list ----------
  const viewRef = useRef(view);
  const loadedCountRef = useRef(0);
  const firstLoadRef = useRef(loadSeen() === null);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await api.getWidgetSessionCounts(getActiveAiAgentId());
      if (res) setCounts(res);
    } catch (e) {
      // counts are optional
    }
  }, [getActiveAiAgentId]);

  // mode: 'reset' (first page of the tab), 'more' (next page), 'refresh' (reload what is shown)
  const fetchSessions = useCallback(async (mode = 'refresh') => {
    const aiAgentId = getActiveAiAgentId();
    const currentView = viewRef.current;
    try {
      setIsSessionsLoading(true);
      let page = 1;
      let limit = PAGE_SIZE;
      if (mode === 'more') page = Math.floor(loadedCountRef.current / PAGE_SIZE) + 1;
      if (mode === 'refresh') limit = Math.min(100, Math.max(PAGE_SIZE, loadedCountRef.current));
      const data = await api.getWidgetSessions(aiAgentId, page, limit, false, currentView);
      if (viewRef.current !== currentView) return; // tab changed meanwhile
      const list = data.sessions || [];
      setSessions(prev => {
        let next = list;
        if (mode === 'more') {
          const ids = new Set(prev.map(s => s.session_id));
          next = [...prev, ...list.filter(s => !ids.has(s.session_id))];
        }
        loadedCountRef.current = next.length;
        setHasMore(next.length < (data.total_count || 0));
        return next;
      });
      // the very first visit: what is already there counts as read
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        setSeen(prev => {
          const seeded = { ...prev };
          list.forEach(s => { if (!seeded[s.session_id]) seeded[s.session_id] = toMs(s.updated_at); });
          return seeded;
        });
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setIsSessionsLoading(false);
    }
    fetchCounts();
  }, [getActiveAiAgentId, fetchCounts]);

  const fetchTeam = useCallback(() => {
    api.get('/live-chat/agents', { skipToast: true }).then(setTeam).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTeam();
    const t = setInterval(fetchTeam, TEAM_REFRESH_MS);
    const onPresence = () => fetchTeam();
    window.addEventListener(PRESENCE_EVENT, onPresence);
    return () => {
      clearInterval(t);
      window.removeEventListener(PRESENCE_EVENT, onPresence);
    };
  }, [fetchTeam]);

  useEffect(() => {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen)); } catch (e) { /* storage full or blocked */ }
  }, [seen]);

  const changeView = useCallback((v) => {
    if (v === viewRef.current) return;
    viewRef.current = v;
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
    setSessions([]);
    loadedCountRef.current = 0;
    fetchSessions('reset');
  }, [fetchSessions]);

  const sessionIdRef = useRef(sessionId);
  const fetchSessionsRef = useRef(fetchSessions);
  const patchOpenSessionRef = useRef(() => {});

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    fetchSessionsRef.current = fetchSessions;
  }, [fetchSessions]);

  useEffect(() => {
    fetchSessions('reset');
  }, [fetchSessions]);

  // the open chat keeps its latest data even when it is not in the current tab
  useEffect(() => {
    const fromList = sessions.find(s => s.session_id === sessionId);
    if (fromList) setSelectedSession(fromList);
  }, [sessions, sessionId]);

  const currentSession = sessions.find(s => s.session_id === sessionId) || (selectedSession && selectedSession.session_id === sessionId ? selectedSession : null);

  const unreadIds = useMemo(() => {
    const ids = new Set();
    sessions.forEach(s => {
      if (s.session_id === sessionId) return;
      const updated = toMs(s.updated_at);
      const lastSeen = seen[s.session_id];
      if (s.last_message_role === 'user' && updated && (lastSeen === undefined ? isWaiting(s) : updated > lastSeen + 1000)) ids.add(s.session_id);
    });
    return ids;
  }, [sessions, seen, sessionId]);

  // mark the open chat as read
  useEffect(() => {
    if (currentSession) {
      const updated = toMs(currentSession.updated_at) || Date.now();
      setSeen(prev => (prev[currentSession.session_id] >= updated ? prev : { ...prev, [currentSession.session_id]: updated }));
    }
  }, [currentSession]);

  const loadChatHistory = useCallback(async (id = sessionId) => {
    if (!id) return;
    try {
      const aiAgentId = getActiveAiAgentId();
      const history = await api.getWidgetHistory(id, aiAgentId, true);

      // Clear before reloading history to prevent stale dedup keys
      addedMessageTextsRef.current.clear();

      if (history && history.length > 0) {
        // an image or file without text is still a message (it was dropped before)
        const restored = history
          .map(msg => ({ ...msg, message: historyText(msg) }))
          .filter(msg => msg.message.trim() !== '')
          .map((msg, idx) => {
            const cleanedText = cleanText(msg.message);
            const senderRole = msg.type;
            // Register key to prevent future duplicates in WebSocket broadcasts
            addedMessageTextsRef.current.add(`${senderRole}_${cleanedText}`);
            const ts = toMs(msg.timestamp);
            return { id: `history_${idx}`, text: cleanedText, sender: senderRole, ts, timestamp: ts ? timeLabel(ts) : '' };
          });
        setMessages(restored);
      } else {
        setMessages([]);
      }
      setHistoryLoaded(true);
    } catch (err) {
      console.error('Error loading chat history:', err);
    }
  }, [sessionId, getActiveAiAgentId]);

  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
  }, [sessionId, loadChatHistory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectSession = useCallback((session) => {
    const id = session.session_id;
    if (id === sessionIdRef.current) return;
    setSessionId(id);
    setSelectedSession(session);
    localStorage.setItem('human_agent_interface_session_id', id);
    setMessages([]);
    setHistoryLoaded(false);
    setPrivateNoteMode(false);
    setLocalTags([]);

    const isAccepted = session.handoff_status === 'active' || session.handoff_status === 'claimed';
    setIsHandoffAccepted(isAccepted);
    setIsResolved(session.handoff_status === 'resolved');
    setIsTakenOver(session.is_taken_over || !!session.assigned_to || false);
    setTakenOverBy(session.taken_over_by || session.assigned_to || null);
    setTakenOverByName(session.taken_over_by_name || session.assigned_to_name || null);
  }, []);

  // keep the open chat's status current even when it moved to another tab
  const patchOpenSession = useCallback((id, patch) => {
    setSelectedSession(prev => (prev && prev.session_id === id ? { ...prev, ...patch } : prev));
    setSessions(prev => prev.map(s => (s.session_id === id ? { ...s, ...patch } : s)));
  }, []);

  useEffect(() => {
    patchOpenSessionRef.current = patchOpenSession;
  }, [patchOpenSession]);

  const handleTakeOverChat = async () => {
    try {
      const aiAgentId = getActiveAiAgentId();
      await api.post(`/widget/sessions/${sessionId}/takeover`, {}, { aiAgentId });
      setIsHandoffAccepted(true);
      setIsResolved(false);
      setIsTakenOver(true);
      setTakenOverBy(user?.human_agent_id || user?.username || null);
      setTakenOverByName(user?.username || user?.name || null);
      setPrivateNoteMode(false);
      patchOpenSession(sessionId, {
        handoff_status: 'active', is_taken_over: true,
        assigned_to: user?.human_agent_id || user?.username || null, assigned_to_name: user?.username || user?.name || null,
      });
      // the chat now belongs to me: show it in Mine
      if (viewRef.current !== 'mine') changeView('mine');
      else fetchSessions('refresh');
      setIsHeaderMenuOpen(false);
      showToast('You are now handling this chat');
      setTimeout(() => editorRef.current && editorRef.current.focus(), 50);
    } catch (err) {
      console.error('Error taking over chat:', err);
      showToast('Failed to take over chat');
    }
  };

  // WebSocket integration
  useEffect(() => {
    if (!user) return;

    let socket = null;
    let reconnectTimeout = null;
    let inactivityTimeout = null;
    let isComponentMounted = true;

    const resetInactivityTimeout = () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      if (isInactiveRef.current) {
        setIsInactive(false);
        isInactiveRef.current = false;
        connectWebSocket();
      }
      inactivityTimeout = setTimeout(() => {
        setIsInactive(true);
        isInactiveRef.current = true;
        if (socket) socket.close();
      }, 1800000);
    };

    const events = ['mousemove', 'keypress', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));

    resetInactivityTimeout();

    const notifyWaiting = (data) => {
      beep();
      try {
        if ('Notification' in window && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
          const n = new Notification('Visitor waiting for an agent', { body: data.reason || 'Open EasyChat to answer.', tag: `lc_${data.session_id}` });
          n.onclick = () => { window.focus(); n.close(); };
        }
      } catch (e) {
        // notifications blocked
      }
    };

    const connectWebSocket = () => {
      if (!isComponentMounted) return;
      if (isInactiveRef.current) return;

      const aiAgentId = getActiveAiAgentId();
      let token = null;
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          token = userData.token;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      if (!token) return;

      const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
      const isHttps = apiBase.startsWith('https://') || window.location.protocol === 'https:';
      const protocol = isHttps ? 'wss:' : 'ws:';
      const wsHost = apiBase.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${protocol}//${wsHost}/api/ws/ai-agents?ai_agent_id=${aiAgentId}&token=${token}`;

      socket = new WebSocket(wsUrl);

      const lastEventRef = { id: null, type: null, time: 0 };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const nowMs = Date.now();
          if (data.session_id === lastEventRef.id && data.type === lastEventRef.type && (nowMs - lastEventRef.time) < 100) return;
          lastEventRef.id = data.session_id;
          lastEventRef.type = data.type;
          lastEventRef.time = nowMs;

          const currentActiveSessionId = sessionIdRef.current;
          const refresh = () => fetchSessionsRef.current && fetchSessionsRef.current('refresh');

          switch (data.type) {
            case 'SESSION_STATUS':
              if (data.session_id) {
                if (data.status === 'requested') {
                  setSessions(prev => prev.map(s =>
                    s.session_id === data.session_id
                      ? { ...s, handoff_status: 'requested', last_reason: data.reason }
                      : s
                  ));
                  notifyWaiting(data);
                }
                refresh();
              }
              break;
            case 'HANDOFF_REQUESTED':
              notifyWaiting(data);
              refresh();
              break;
            case 'NEW_MESSAGE':
              if (data.session_id === currentActiveSessionId) {
                const cleanedText = cleanText(data.message.content);
                const incomingRole = data.message.role;
                const dedupKey = `${incomingRole}_${cleanedText}`;

                // Synchronous check & set to immediately abort duplicate WebSocket events in the same event loop tick
                if (addedMessageTextsRef.current.has(dedupKey)) {
                  break;
                }
                addedMessageTextsRef.current.add(dedupKey);

                setMessages(prev => {
                  const exists = prev.some(m => m.text.trim() === cleanedText && m.sender === incomingRole);
                  if (exists) return prev;
                  return [...prev, { id: Date.now() + Math.random(), text: cleanedText, sender: incomingRole, ts: Date.now(), timestamp: timeLabel() }];
                });
              }
              refresh();
              break;
            case 'PRIVATE_NOTE':
              if (data.session_id === currentActiveSessionId) {
                setMessages(prev => {
                  const cleanedText = String(data.message.content).trim();
                  const exists = prev.some(m => m.text.trim() === cleanedText && m.sender === 'private_note');
                  if (exists) return prev;
                  return [...prev, { id: Date.now() + Math.random(), text: cleanedText, sender: 'private_note', ts: Date.now(), timestamp: timeLabel() }];
                });
              }
              refresh();
              break;
            case 'SESSION_CLAIMED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(true);
                if (data.assigned_to) setTakenOverBy(data.assigned_to);
                if (data.assigned_to_name) setTakenOverByName(data.assigned_to_name);
                patchOpenSessionRef.current(data.session_id, { handoff_status: 'active', assigned_to: data.assigned_to, assigned_to_name: data.assigned_to_name });
              }
              refresh();
              break;
            case 'SESSION_RESOLVED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(false);
                setIsResolved(true);
                patchOpenSessionRef.current(data.session_id, { handoff_status: 'resolved' });
              }
              refresh();
              break;
            case 'SESSION_TAKEOVER':
              if (data.session_id === currentActiveSessionId) {
                setIsTakenOver(true);
                setTakenOverBy(data.taken_over_by);
                setTakenOverByName(data.taken_over_by_name);
              }
              refresh();
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('WS Parsing Error:', err);
        }
      };

      socket.onclose = () => {
        if (!isComponentMounted) return;
        if (isInactiveRef.current) return;
        reconnectTimeout = setTimeout(connectWebSocket, 3000);
      };
    };

    connectWebSocket();

    return () => {
      isComponentMounted = false;
      if (socket) socket.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendMessage = async () => {
    if ((!inputMessage.trim() && !attachedFile) || isLoading) return;
    if (!sessionId) {
      showToast('Select a chat to send a message.');
      return;
    }

    const currentInput = inputMessage;
    const currentHtml = editorHtml;
    const fileToUpload = attachedFile;

    setInputMessage('');
    setEditorHtml('');
    setAttachedFile(null);
    if (editorRef.current) editorRef.current.innerHTML = '';

    if (privateNoteMode) {
      setIsLoading(true);
      try {
        const aiAgentId = getActiveAiAgentId();
        await api.sendPrivateNote(sessionId, currentInput, aiAgentId);

        const note = { id: `private_${Date.now()}`, text: currentInput, sender: 'private_note', ts: Date.now(), timestamp: timeLabel() };
        addedMessageTextsRef.current.add(`private_note_${currentInput.trim()}`);
        setMessages(prev => {
          const exists = prev.some(m => m.text.trim() === currentInput.trim() && m.sender === 'private_note');
          if (exists) return prev;
          return [...prev, note];
        });
        showToast('Private note saved to this chat.');
      } catch (err) {
        console.error('Error sending private note:', err);
        showToast('Failed to save private note.');
        setInputMessage(currentInput);
        if (editorRef.current) editorRef.current.innerHTML = currentHtml;
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!isHandoffAccepted) return;

    setIsLoading(true);

    try {
      let imageBase64 = null;
      let fileBase64 = null;

      if (fileToUpload) {
        const fileReader = new FileReader();
        const readPromise = new Promise((resolve, reject) => {
          fileReader.onload = () => resolve(fileReader.result);
          fileReader.onerror = (e) => reject(e);
        });
        fileReader.readAsDataURL(fileToUpload);
        const base64Data = await readPromise;

        if (fileToUpload.type.startsWith('image/')) {
          imageBase64 = base64Data;
        } else {
          fileBase64 = base64Data;
        }
      }

      const aiAgentId = getActiveAiAgentId();
      await api.post('/widget/chat/agent-message', {
        session_id: sessionId,
        message: currentInput,
        image: imageBase64,
        file: fileBase64,
        metadata: fileToUpload ? { filename: fileToUpload.name } : {}
      }, { aiAgentId });

      // Reload chat history to pull the newly uploaded file/image with its correct server path
      await loadChatHistory(sessionId);
      fetchSessions('refresh');
    } catch (error) {
      console.error('Chat error:', error);
      setInputMessage(currentInput);
      setEditorHtml(currentHtml);
      if (editorRef.current) editorRef.current.innerHTML = currentHtml;
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveHandoff = useCallback(async () => {
    try {
      const aiAgentId = getActiveAiAgentId();
      await api.post('/widget/handoff/resolve', { session_id: sessionId }, { aiAgentId });
      setIsHandoffAccepted(false);
      setIsResolved(true);
      patchOpenSession(sessionId, { handoff_status: 'resolved', is_taken_over: false });
      fetchSessions('refresh');
      showToast('Chat resolved. The bot answers this visitor again.');
    } catch (err) {
      console.error('Resolve error:', err);
    }
  }, [sessionId, fetchSessions, getActiveAiAgentId, showToast, patchOpenSession]);

  const getTagLabel = (tag) => {
    if (!tag) return '';
    return typeof tag === 'string' ? tag : tag.name || String(tag);
  };

  const convertHtmlToMarkdown = (html) => {
    if (!html) return '';
    let md = html;
    md = md.replace(/<div><br><\/div>/gi, '\n');
    md = md.replace(/<div[^>]*>/gi, '\n');
    md = md.replace(/<\/div>/gi, '');
    md = md.replace(/<p[^>]*>/gi, '');
    md = md.replace(/<\/p>/gi, '\n');
    md = md.replace(/<br\s*\/?>/gi, '\n');
    md = md.replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, '**$1**');
    md = md.replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, '**$1**');
    md = md.replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, '*$1*');
    md = md.replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, '*$1*');
    md = md.replace(/<strike[^>]*>([\s\S]*?)<\/strike>/gi, '~~$1~~');
    md = md.replace(/<s[^>]*>([\s\S]*?)<\/s>/gi, '~~$1~~');
    md = md.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, p1) => {
      return '\n' + p1.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n') + '\n';
    });
    md = md.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, p1) => {
      let count = 1;
      return '\n' + p1.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (m, p) => `${count++}. ${p}\n`) + '\n';
    });
    // Mask span color styles, font color tags, and underline tags so they are not stripped
    md = md.replace(/<span style="color:\s*([^;"]+);?">([\s\S]*?)<\/span>/gi, '__SPAN_COLOR_START_($1)__$2__SPAN_COLOR_END__');
    md = md.replace(/<span style='color:\s*([^;']+);?'>([\s\S]*?)<\/span>/gi, '__SPAN_COLOR_START_($1)__$2__SPAN_COLOR_END__');
    md = md.replace(/<font color="([^"]+)">([\s\S]*?)<\/font>/gi, '__FONT_COLOR_START_($1)__$2__FONT_COLOR_END__');
    md = md.replace(/<font color='([^']+)'>([\s\S]*?)<\/font>/gi, '__FONT_COLOR_START_($1)__$2__FONT_COLOR_END__');
    md = md.replace(/<u[^>]*>([\s\S]*?)<\/u>/gi, '__UNDERLINE_START__$1__UNDERLINE_END__');

    md = md.replace(/<[^>]+>/g, '');

    // Restore color and underline tags
    md = md.replace(/__SPAN_COLOR_START_\(([^)]+)\)__/g, '<span style="color:$1">');
    md = md.replace(/__SPAN_COLOR_END__/g, '</span>');
    md = md.replace(/__FONT_COLOR_START_\(([^)]+)\)__/g, '<font color="$1">');
    md = md.replace(/__FONT_COLOR_END__/g, '</font>');
    md = md.replace(/__UNDERLINE_START__/g, '<u>');
    md = md.replace(/__UNDERLINE_END__/g, '</u>');

    md = md.replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    md = md.replace(/\n{3,}/g, '\n\n');
    return md.trim();
  };

  const updateFormattingState = () => {
    if (!editorRef.current) return;
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList')
    });
  };

  const syncEditorState = () => {
    const html = editorRef.current?.innerHTML || '';
    const text = convertHtmlToMarkdown(html);
    setEditorHtml(html);
    setInputMessage(text);
    updateFormattingState();
  };

  const execEditorCommand = (command, value = null) => {
    if (!editorRef.current || !sessionId || (!isHandoffAccepted && !privateNoteMode)) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    if (command !== 'foreColor') setActiveFormat(null);
    syncEditorState();
  };

  const applyColor = (color) => {
    setCurrentColor(color);
    execEditorCommand('foreColor', color);
    setColorPickerOpen(false);
  };

  const clearFormatting = () => {
    if (!editorRef.current || !sessionId || (!isHandoffAccepted && !privateNoteMode)) return;
    editorRef.current.focus();
    document.execCommand('removeFormat', false, null);
    document.execCommand('unlink', false, null);

    if (document.queryCommandState('insertUnorderedList')) {
      document.execCommand('insertUnorderedList', false, null);
    }
    if (document.queryCommandState('insertOrderedList')) {
      document.execCommand('insertOrderedList', false, null);
    }

    setCurrentColor('#111827');
    setActiveFormat(null);
    syncEditorState();
  };

  const handleAddTag = () => {
    const tagValue = window.prompt('Enter new tag name:');
    if (!tagValue || !tagValue.trim()) return;

    const sanitized = tagValue.replace(/[^a-zA-Z0-9-_ ]+/g, '').trim();
    if (!sanitized) {
      showToast('Tag must contain letters or numbers.');
      return;
    }
    setLocalTags(prev => prev.includes(sanitized) ? prev : [...prev, sanitized]);
    showToast(`Tag added: ${sanitized}`);
  };

  useEffect(() => {
    if (currentSession) {
      setIsHandoffAccepted(currentSession.handoff_status === 'active');
      setIsResolved(currentSession.handoff_status === 'resolved');
      setIsTakenOver(currentSession.is_taken_over || !!currentSession.assigned_to || false);
      setTakenOverBy(currentSession.taken_over_by || currentSession.assigned_to || null);
      setTakenOverByName(currentSession.taken_over_by_name || currentSession.assigned_to_name || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, currentSession && currentSession.handoff_status, currentSession && currentSession.assigned_to]);

  const isTakenOverByOther = isTakenOver &&
    String(takenOverBy) !== String(user?.id) &&
    String(takenOverBy) !== String(user?.username) &&
    String(takenOverBy) !== String(user?.human_agent_id);
  const canReply = !!sessionId && !isResolved && isHandoffAccepted && !isTakenOverByOther;
  const showEditor = !!sessionId && !isResolved && (privateNoteMode || canReply);
  const isInputDisabled = !showEditor;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePaste = (e) => {
    if (isInputDisabled) return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          const renamedFile = new File([file], file.name || `pasted-image-${Date.now()}.png`, { type: file.type });
          setAttachedFile(renamedFile);
          showToast(`Image pasted: ${renamedFile.name}`);
        }
      }
    }
  };

  const attachFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setAttachedFile(file);
        showToast(`File attached: ${file.name}`);
      }
    };
    input.click();
  };

  // Keyboard: Alt+Up / Alt+Down previous / next chat, "/" search
  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        const idx = sessions.findIndex(s => s.session_id === sessionIdRef.current);
        const next = sessions[e.key === 'ArrowDown' ? idx + 1 : Math.max(0, idx - 1)];
        if (next) handleSelectSession(next);
      } else if (e.key === '/' && !typing) {
        e.preventDefault();
        window.dispatchEvent(new Event(OPEN_SEARCH_EVENT));
        setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sessions, handleSelectSession]);

  const combinedTags = Array.from(new Set([...(currentSession?.tags || []).map(getTagLabel), ...localTags]));
  const waitingSince = currentSession ? (toMs(currentSession.handoff_requested_at) || toMs(currentSession.updated_at)) : 0;
  const oldestWaiting = view === 'waiting' && sessions.length ? (toMs(sessions[0].handoff_requested_at) || toMs(sessions[0].updated_at)) : 0;
  const statusLabel = currentSession ? liveChatStatusLabel(currentSession.handoff_status) : '';
  const statusVariant = currentSession && isWaiting(currentSession) ? 'warning' : currentSession && isLive(currentSession) ? 'success' : 'default';

  const formatButtons = [
    { command: 'bold', icon: Bold, label: 'Bold', active: activeFormats.bold },
    { command: 'italic', icon: Italic, label: 'Italic', active: activeFormats.italic },
    { command: 'underline', icon: Underline, label: 'Underline', active: activeFormats.underline },
    { command: 'strikeThrough', icon: Strikethrough, label: 'Strikethrough', active: activeFormats.strikeThrough },
    { command: 'insertUnorderedList', icon: List, label: 'Bullet list', active: activeFormats.insertUnorderedList },
    { command: 'insertOrderedList', icon: ListOrdered, label: 'Numbered list', active: activeFormats.insertOrderedList }
  ];

  const renderComposer = () => {
    if (!sessionId) return null;
    if (isResolved) {
      return (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
          <span className="flex items-center gap-2 text-[13px] text-zinc-600"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> This chat is resolved. The bot answers the visitor again.</span>
        </div>
      );
    }
    if (!showEditor) {
      const waiting = currentSession && isWaiting(currentSession);
      return (
        <div className={cn('flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3', waiting ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-zinc-50')}>
          <p className={cn('flex-1 text-[13px]', waiting ? 'text-amber-900' : 'text-zinc-600')}>
            {isTakenOverByOther
              ? <><span className="font-medium">{takenOverByName || 'Another agent'}</span> is handling this chat.</>
              : waiting
                ? <>The visitor is waiting for an agent{waitingSince ? ` (${shortAgo(waitingSince, now) === 'now' ? 'under a minute' : shortAgo(waitingSince, now)})` : ''}.</>
                : <>The bot is handling this chat. Take over to reply yourself.</>}
          </p>
          <Button variant="outline" size="sm" onClick={() => setPrivateNoteMode(true)}>
            <Lock className="h-3.5 w-3.5" /> Private note
          </Button>
          {isTakenOverByOther ? (
            isAdmin && <Button size="sm" onClick={() => setShowReassign(true)}><ArrowRightLeft className="h-3.5 w-3.5" /> Reassign</Button>
          ) : (
            <Button size="sm" onClick={handleTakeOverChat}><Headphones className="h-3.5 w-3.5" /> Take over chat</Button>
          )}
        </div>
      );
    }

    const canSend = !!(inputMessage.trim() || attachedFile) && !isLoading;
    return (
      <div className={cn('overflow-hidden rounded-xl border bg-white transition-colors', privateNoteMode ? 'border-amber-300' : 'border-zinc-200 focus-within:border-zinc-400')}>
        <div className={cn('flex items-center gap-1 overflow-x-auto whitespace-nowrap border-b px-2 py-1.5', privateNoteMode ? 'border-amber-200 bg-amber-50' : 'border-zinc-100 bg-zinc-50')}>
          <div className="mr-1 flex shrink-0 rounded-md border border-zinc-200 bg-white p-0.5">
            <button type="button" disabled={!canReply} onClick={() => setPrivateNoteMode(false)}
              className={cn('flex items-center gap-1 rounded px-2 py-1 text-xs font-medium', !privateNoteMode ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100', !canReply && 'cursor-not-allowed opacity-50')}
              title={canReply ? 'Reply to the visitor' : 'Take over the chat to reply'}>
              <MessageSquare className="h-3 w-3" /> Reply
            </button>
            <button type="button" onClick={() => setPrivateNoteMode(true)}
              className={cn('flex items-center gap-1 rounded px-2 py-1 text-xs font-medium', privateNoteMode ? 'bg-amber-500 text-white' : 'text-zinc-600 hover:bg-zinc-100')}>
              <Lock className="h-3 w-3" /> Private note
            </button>
          </div>
          {formatButtons.map((btn) => {
            const Icon = btn.icon;
            return (
              <button key={btn.command} type="button" title={btn.label} aria-label={btn.label} onClick={() => execEditorCommand(btn.command)}
                className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded', btn.active ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-200/60 hover:text-zinc-900')}>
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
          <div className="relative">
            <button type="button" title="Text colour" aria-label="Text colour" onClick={() => { setActiveFormat('foreColor'); setColorPickerOpen(p => !p); }}
              className={cn('flex h-7 w-7 items-center justify-center rounded', activeFormat === 'foreColor' ? 'bg-zinc-200' : 'text-zinc-500 hover:bg-zinc-200/60')}>
              <Palette className="h-3.5 w-3.5" style={{ color: currentColor }} />
            </button>
            {colorPickerOpen && (
              <div className="absolute left-0 top-8 z-40 flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-lg">
                {['#18181b', '#2563eb', '#dc2626', '#16a34a', '#d97706'].map(col => (
                  <button key={col} type="button" onClick={() => applyColor(col)} title={col} className="h-5 w-5 rounded border border-zinc-200" style={{ background: col }} />
                ))}
                <input type="color" value={currentColor} onChange={(e) => applyColor(e.target.value)} className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0" />
              </div>
            )}
          </div>
          <button type="button" title="Clear formatting" aria-label="Clear formatting" onClick={clearFormatting}
            className="flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-200/60 hover:text-zinc-900">
            <Eraser className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="relative">
          <div
            ref={editorRef}
            className="rich-text-editor max-h-40 min-h-[56px] overflow-y-auto whitespace-pre-wrap break-words px-3 py-2.5 text-sm text-zinc-900 outline-none"
            contentEditable
            suppressContentEditableWarning={true}
            onInput={syncEditorState}
            onKeyDown={handleKeyPress}
            onPaste={handlePaste}
            onKeyUp={updateFormattingState}
            onMouseUp={updateFormattingState}
          />
          {!editorHtml && (
            <div className="pointer-events-none absolute left-3 top-2.5 text-sm text-zinc-400">
              {privateNoteMode ? 'Write a note for your team. The visitor will not see it.' : 'Type your reply. Enter to send, Shift+Enter for a new line.'}
            </div>
          )}
        </div>

        {attachedFile && (
          <div className="mx-3 mb-2 flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5">
            <span className="flex min-w-0 items-center gap-2 text-xs text-zinc-700"><Paperclip className="h-3.5 w-3.5 shrink-0 text-zinc-400" /><span className="truncate">{attachedFile.name}</span></span>
            <button type="button" onClick={() => setAttachedFile(null)} aria-label="Remove file" className="text-zinc-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-zinc-100 px-2 py-1.5">
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="icon" onClick={attachFile} title="Attach file" aria-label="Attach file"><Paperclip className="h-4 w-4" /></Button>
            <div className="relative">
              <Button variant="ghost" size="icon" onClick={() => setEmojiPickerOpen(p => !p)} title="Emoji" aria-label="Emoji"><Smile className="h-4 w-4" /></Button>
              {emojiPickerOpen && (
                <div className="absolute bottom-10 left-0 z-40">
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      if (editorRef.current) {
                        editorRef.current.focus();
                        document.execCommand('insertText', false, emojiData.emoji);
                        syncEditorState();
                      }
                      setEmojiPickerOpen(false);
                    }}
                    height={320}
                    width={280}
                    previewConfig={{ showPreview: false }}
                  />
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={handleAddTag} title="Add tag" aria-label="Add tag"><Tag className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2">
            {canReply && !privateNoteMode && (
              <Button variant="outline" size="sm" onClick={handleResolveHandoff}><CheckCircle2 className="h-3.5 w-3.5" /> Resolve</Button>
            )}
            <Button size="sm" variant={privateNoteMode ? 'warning' : 'default'} onClick={sendMessage} disabled={!canSend}>
              <Send className="h-3.5 w-3.5" /> {privateNoteMode ? 'Save note' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const onlineCount = team ? team.online_count : null;

  return (
    <div className={cn('flex flex-col overflow-hidden bg-zinc-50', isAdmin ? 'h-full' : 'h-screen')}>
      {showReassign && sessionId && (
        <ReassignChat
          sessionId={sessionId}
          aiAgentId={getActiveAiAgentId()}
          onClose={() => setShowReassign(false)}
          onDone={() => fetchSessions('refresh')}
        />
      )}

      {/* Top bar for agents (admins get the app navbar) */}
      {!isAdmin && (
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-5">
          <h1 className="text-[15px] font-semibold text-zinc-900">Live chat</h1>
          <div className="flex items-center gap-3">
            {user?.name && <span className="text-xs text-zinc-500">{user.name}</span>}
            <PresenceSwitch />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-2 overflow-hidden p-2">
        <AwayBar team={team} waitingCount={counts ? counts.waiting : 0} oldestWaitingMs={oldestWaiting} now={now} onPresenceChanged={fetchTeam} />

        {isInactive && (
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <p className="flex-1 text-[13px] text-amber-900">Paused after 30 minutes without activity. New messages are not shown until you reconnect.</p>
            <Button variant="warning" size="sm" onClick={() => window.dispatchEvent(new Event('mousemove'))}>Reconnect</Button>
          </div>
        )}

        <div className="flex flex-1 gap-2 overflow-hidden">
          <ChatList
            sessions={sessions}
            activeSessionId={sessionId}
            onSelect={handleSelectSession}
            view={view}
            onViewChange={changeView}
            counts={counts}
            isLoading={isSessionsLoading}
            hasMore={hasMore}
            onLoadMore={() => fetchSessions('more')}
            unreadIds={unreadIds}
            now={now}
            team={team}
            user={user}
            onLogout={onLogout}
            searchRef={searchRef}
          />

          {/* Conversation */}
          <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white">
            {!sessionId || !currentSession ? (
              <div className="flex flex-1 flex-col items-center justify-center p-8">
                <div className="w-full max-w-xl">
                  <h2 className="text-base font-semibold text-zinc-900">Today's queue</h2>
                  <p className="mt-1 text-sm text-zinc-500">Pick a chat on the left. Waiting chats are sorted by who has waited longest.</p>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatTile icon={Clock} label="Waiting" value={counts?.waiting} tone="text-amber-600" />
                    <StatTile icon={Inbox} label="With agents" value={counts?.team} tone="text-emerald-600" />
                    <StatTile icon={BotIcon} label="With the bot" value={counts?.bot} tone="text-zinc-500" />
                    <StatTile icon={Users} label="Agents online" value={onlineCount} tone="text-sky-600" />
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                    <span><Kbd>Alt</Kbd> + <Kbd>↑</Kbd> <Kbd>↓</Kbd> previous / next chat</span>
                    <span><Kbd>/</Kbd> search</span>
                    <span><Kbd>Enter</Kbd> send</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <header className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3">
                  <Avatar name={visitorName(currentSession)} seed={currentSession.session_id} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate text-sm font-semibold text-zinc-900">{visitorName(currentSession)}</h2>
                      {statusLabel && <Badge variant={statusVariant}>{statusLabel}{isWaiting(currentSession) && waitingSince ? ` · ${shortAgo(waitingSince, now) === 'now' ? '<1m' : shortAgo(waitingSince, now)}` : ''}</Badge>}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                      <ChannelIcon session={currentSession} />
                      <span>{CHANNEL_LABELS[channelOf(currentSession)]}</span>
                      {(takenOverByName || currentSession.assigned_to_name) && isLive(currentSession) && (
                        <><span className="text-zinc-300">·</span><span>Agent: {takenOverByName || currentSession.assigned_to_name}</span></>
                      )}
                      {currentSession.location && <><span className="text-zinc-300">·</span><span className="truncate">{currentSession.location}</span></>}
                    </div>
                  </div>
                  {combinedTags.length > 0 && (
                    <div className="hidden max-w-[200px] flex-wrap justify-end gap-1 lg:flex">
                      {combinedTags.slice(0, 3).map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                    </div>
                  )}
                  {!isResolved && (
                    <Button variant="outline" size="sm" onClick={() => setShowReassign(true)} title="Transfer to another agent or group">
                      <ArrowRightLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Transfer</span>
                    </Button>
                  )}
                  {!isResolved && (
                    <div ref={headerMenuRef} className="relative">
                      <Button variant="ghost" size="icon" onClick={() => setIsHeaderMenuOpen(p => !p)} aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></Button>
                      {isHeaderMenuOpen && (
                        <div className="absolute right-0 top-10 z-40 w-48 rounded-lg border border-zinc-200 bg-white p-1 shadow-lg">
                          {!canReply && !isTakenOverByOther && (
                            <button type="button" onClick={handleTakeOverChat} className="w-full rounded px-2.5 py-1.5 text-left text-xs text-zinc-800 hover:bg-zinc-100">Take over chat</button>
                          )}
                          {isAdmin && isTakenOverByOther && (
                            <button type="button" onClick={handleTakeOverChat} className="w-full rounded px-2.5 py-1.5 text-left text-xs text-zinc-800 hover:bg-zinc-100">Take over from {takenOverByName || 'agent'}</button>
                          )}
                          <button type="button" onClick={() => { setIsHeaderMenuOpen(false); setShowReassign(true); }} className="w-full rounded px-2.5 py-1.5 text-left text-xs text-zinc-800 hover:bg-zinc-100">Transfer chat</button>
                          {(isLive(currentSession) || isWaiting(currentSession)) && (
                            <button type="button" onClick={() => { setIsHeaderMenuOpen(false); handleResolveHandoff(); }} className="w-full rounded px-2.5 py-1.5 text-left text-xs text-red-600 hover:bg-red-50">Resolve chat</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </header>

                <div ref={messagesBoxRef} className="flex-1 overflow-y-auto bg-zinc-50/60 px-5 py-4 custom-scrollbar">
                  {!historyLoaded ? (
                    <div className="space-y-4 py-4">
                      {[0, 1, 2].map(i => (
                        <div key={i} className={cn('flex animate-pulse gap-2', i % 2 && 'flex-row-reverse')}>
                          <div className="h-7 w-7 rounded-full bg-zinc-200" />
                          <div className="h-14 w-1/2 rounded-xl bg-zinc-200/70" />
                        </div>
                      ))}
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="py-10 text-center text-sm text-zinc-500">No messages in this conversation yet.</p>
                  ) : (
                    <MessageList messages={messages} session={currentSession} isResolved={isResolved} />
                  )}
                </div>

                <div className="border-t border-zinc-200 bg-white p-3">
                  {renderComposer()}
                </div>
              </>
            )}
          </section>

          <DetailsPanel session={currentSession} messages={messages} tags={combinedTags} />
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d4d4d8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a1a1aa; }
        .markdown-content p { margin: 0; }
        .markdown-content p + p { margin-top: 0.5em; }
        .markdown-content ul, .markdown-content ol { margin: 0.25em 0; padding-left: 1.25em; }
        .markdown-content ul { list-style: disc; }
        .markdown-content ol { list-style: decimal; }
        .markdown-content a { text-decoration: underline; }
        .rich-text-editor ul { list-style: disc; padding-left: 1.25em; }
        .rich-text-editor ol { list-style: decimal; padding-left: 1.25em; }
      `}</style>
    </div>
  );
};

export default HumanAgentChatInterface;
